import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ChevronLeft, ChevronRight, ShoppingCart, ArrowLeft } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toast } = useToast();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    fetchProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await fetch(`${API}/products/${id}`);
      if (response.ok) {
        const data = await response.json();
        setProduct(data);
      } else {
        console.error('Product not found, status:', response.status);
        setProduct(null);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      setProduct(null);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (product) {
      addToCart({
        id: product.id,
        name: product.name,
        price: product.price,
        currency: product.currency,
        image: product.image
      });
      toast({
        title: "¡Agregado al carrito!",
        description: `${product.name} fue agregado correctamente`
      });
    }
  };

  const nextImage = () => {
    const images = [product.image, ...(product.images || [])];
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    const images = [product.image, ...(product.images || [])];
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <p className="text-white text-xl">Cargando...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-xl mb-4">Producto no encontrado</p>
          <Button onClick={() => navigate('/shop')} className="bg-cyan-600">
            Volver a la tienda
          </Button>
        </div>
      </div>
    );
  }

  const images = [product.image, ...(product.images || [])].filter(Boolean);
  const currentImage = images[currentImageIndex];
  const hasMultipleImages = images.length > 1;

  const finalPrice = product.discount > 0 
    ? product.price * (1 - product.discount / 100) 
    : product.price;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        <Button
          variant="ghost"
          onClick={() => navigate('/shop')}
          className="text-white hover:text-cyan-400 mb-6"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Volver a la tienda
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Galería de Imágenes */}
          <div className="space-y-4">
            <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700 overflow-hidden">
              <CardContent className="p-0 relative">
                <div className="aspect-square relative bg-slate-900">
                  <img
                    src={currentImage}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Controles de navegación */}
                  {hasMultipleImages && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-all"
                      >
                        <ChevronLeft className="h-6 w-6" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-all"
                      >
                        <ChevronRight className="h-6 w-6" />
                      </button>
                      
                      {/* Indicador de imágenes */}
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 px-3 py-1 rounded-full">
                        <span className="text-white text-sm">
                          {currentImageIndex + 1} / {images.length}
                        </span>
                      </div>
                    </>
                  )}

                  {/* Badges */}
                  {product.discount > 0 && (
                    <Badge className="absolute top-4 right-4 bg-red-600 text-white text-lg px-3 py-1">
                      -{product.discount}% OFF
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Miniaturas */}
            {hasMultipleImages && (
              <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                      idx === currentImageIndex
                        ? 'border-cyan-500 ring-2 ring-cyan-500/50'
                        : 'border-slate-700 hover:border-slate-500'
                    }`}
                  >
                    <img
                      src={img}
                      alt={`${product.name} ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Información del Producto */}
          <div className="space-y-6">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">{product.name}</h1>
              <Badge className="bg-cyan-600">{product.category}</Badge>
            </div>

            <div className="space-y-2">
              <div className="flex items-baseline gap-3">
                {product.discount > 0 && (
                  <span className="text-2xl text-slate-400 line-through">
                    ${product.price.toLocaleString()} {product.currency}
                  </span>
                )}
                <span className="text-5xl font-bold text-cyan-400">
                  ${finalPrice.toLocaleString()} {product.currency}
                </span>
              </div>
            </div>

            <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
              <CardContent className="p-6">
                <h3 className="text-white font-semibold text-lg mb-3">Descripción</h3>
                <p className="text-slate-300 whitespace-pre-line leading-relaxed">
                  {product.description}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
              <CardContent className="p-6 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Disponibilidad</span>
                  {product.stock_quantity > 0 ? (
                    <Badge className="bg-green-600">
                      En stock ({product.stock_quantity} unidades)
                    </Badge>
                  ) : (
                    <Badge className="bg-red-600">Sin stock</Badge>
                  )}
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Moneda</span>
                  <span className="text-white font-semibold">{product.currency}</span>
                </div>
              </CardContent>
            </Card>

            <Button
              onClick={handleAddToCart}
              disabled={product.stock_quantity === 0}
              className="w-full bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-700 hover:to-emerald-700 text-white text-lg py-6"
            >
              <ShoppingCart className="h-5 w-5 mr-2" />
              {product.stock_quantity > 0 ? 'Agregar al Carrito' : 'Sin Stock'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
